import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { getGroqKey, saveGroqKey, clearGroqKey } from "../History";

const TOOLS = [
  {
    path: "/scribe",
    name: "Scribe",
    tag: "Transcription",
    desc: "Live recording and audio upload with AI formatting into meeting minutes, lecture notes, sermon docs, and song lyrics.",
    accent: "#c8571a",
    accentBg: "#fff3ed",
    accentRgb: "200,87,26",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
      </svg>
    ),
  },
  {
    path: "/interview",
    name: "Interview",
    tag: "Scholarship Prep",
    desc: "AI panel practice with real questions, scored feedback, and model answers for serious scholarship applicants.",
    accent: "#1a7a4a",
    accentBg: "#edfaf3",
    accentRgb: "26,122,74",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    path: "/sop",
    name: "Prose",
    tag: "SOP Builder",
    desc: "Review, build, or write your statement of purpose from scratch. Tailored to your institution, scholarship, and voice.",
    features: ["Review & score", "Build from scratch", "PDF & Word export", "History"],
    accent: "#6a4aaa",
    accentBg: "#f3f0f9",
    accentRgb: "106,74,170",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
  },

  {
    path: "/mirror",
    name: "Mirror",
    tag: "CV Studio",
    desc: "Upload your CV or build one from scratch;  Mirror reviews, rewrites, and tailors it to any role or institution with recruiter or Academics commitee level precision.",
    disabled: true,
    accent: "#1a3a6b",
    accentBg: "#f0f3f9",
    accentRgb: "26,58,107",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
        <path d="M12 2C9 2 7 5 7 8v4c0 3 2 6 5 6s5-3 5-6V8c0-3-2-6-5-6z" />
        <line x1="12" y1="18" x2="12" y2="22" />
        <line x1="8" y1="22" x2="16" y2="22" />
      </svg>
    ),
  },
];

function timeAgo(ts) {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d === 1) return "Yesterday";
  return `${d} days ago`;
}

function getHistory() {
  try {
    return JSON.parse(localStorage.getItem("lliuq_history") || "[]");
  } catch { return []; }
}

function clearHistory() {
  localStorage.removeItem("lliuq_history");
}

export default function Home() {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [activeNav, setActiveNav] = useState("home");
  const [hoveredTool, setHoveredTool] = useState(null);
  const [search, setSearch] = useState("");
  const [renamingIndex, setRenamingIndex] = useState(null);
  const [renameValue, setRenameValue] = useState("")
  const [showMore, setShowMore] = useState(false);
  const [groqKey, setGroqKey] = useState(() => getGroqKey());
  const [isEditing, setIsEditing] = useState(false);
  const [keyInput, setKeyInput] = useState("");

  useEffect(() => {
    setHistory(getHistory());
    const sync = () => setGroqKey(getGroqKey());
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);


  const handleClearHistory = () => {
    clearHistory();
    setHistory([]);
  };

  const handleSaveKey = () => {
    const k = keyInput.trim();
    if (!k.startsWith("gsk_")) return;
    saveGroqKey(k);
    setGroqKey(k);
    setIsEditing(false);
    setKeyInput("");
    window.location.reload();
  };

  const handleClearKey = () => {
    if (window.confirm("Clear all API keys?")) {
      clearGroqKey();
      setGroqKey("");
      setIsEditing(false);
      window.location.reload();
    }
  };

  const handleRename = (index) => {
    const updated = [...history];
    updated[index] = { ...updated[index], title: renameValue };
    localStorage.setItem("lliuq_history", JSON.stringify(updated));
    setHistory(updated);
    setRenamingIndex(null);
    setRenameValue("");
  };

  const toolMap = Object.fromEntries(TOOLS.map(t => [t.path.replace("/", ""), t]));

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');

        .ms-root {
          font-family: 'Inter', -apple-system, sans-serif;
          background: var(--ms-bg, #faf9f8);
          color: var(--ms-text, #1a1a1a);
          min-height: calc(100vh - 60px);
          --ms-bg: #faf9f8;
          --ms-surface: #ffffff;
          --ms-border: rgba(0,0,0,0.08);
          --ms-border-hover: rgba(0,0,0,0.14);
          --ms-text: #1a1a1a;
          --ms-text2: #5a5a5a;
          --ms-text3: #9a9a9a;
          --ms-sidebar-w: 220px;
        }

        /* ── Top bar ── */
        .ms-topbar {
          height: 48px;
          background: #fff;
          border-bottom: 0.5px solid var(--ms-border);
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 20px;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .ms-topbar-left { display: flex; align-items: center; gap: 20px; }

        .ms-waffle {
          width: 32px; height: 32px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 4px;
          cursor: pointer;
          color: var(--ms-text2);
        }
        .ms-waffle:hover { background: #f0f0f0; }

        .ms-topbar-brand {
          font-size: 17px;
          font-weight: 600;
          color: var(--ms-text);
          letter-spacing: -0.3px;
        }
        .ms-topbar-brand em { font-style: normal; color: #c8571a; }

        .ms-topbar-search {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #f3f2f1;
          border: 0.5px solid transparent;
          border-radius: 4px;
          padding: 0 12px;
          height: 34px;
          width: 260px;
          font-size: 14px;
          color: var(--ms-text3);
          cursor: text;
          transition: border-color 0.15s, background 0.15s;
        }
        .ms-topbar-search:hover { background: #ebebeb; }

        .ms-topbar-right { display: flex; align-items: center; gap: 6px; }

        .ms-icon-btn {
          width: 32px; height: 32px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 4px; cursor: pointer; color: var(--ms-text2);
        }
        .ms-icon-btn:hover { background: #f0f0f0; }

        /* ── Layout ── */
        .ms-layout {
          display: grid;
          grid-template-columns: var(--ms-sidebar-w) 1fr;
          min-height: calc(100vh - 108px);
        }

        /* ── Sidebar ── */
        .ms-sidebar {
          background: #fff;
          border-right: 0.5px solid var(--ms-border);
          padding: 12px 0;
          overflow-y: auto;
        }

        .ms-sidebar-section { margin-bottom: 4px; }

        .ms-sidebar-label {
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--ms-text3);
          padding: 10px 16px 4px;
        }

        .ms-sidebar-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 16px;
          font-size: 15px;
          color: var(--ms-text2);
          cursor: pointer;
          border-left: 2px solid transparent;
          transition: background 0.1s, color 0.1s;
        }
        .ms-sidebar-item:hover { background: #f3f2f1; color: var(--ms-text); }
        .ms-sidebar-item.active {
          background: #f3f2f1;
          color: var(--ms-text);
          font-weight: 500;
          border-left-color: #c8571a;
        }
        .ms-sidebar-item.disabled { opacity: 0.45; cursor: default; }
        .ms-sidebar-item.disabled:hover { background: transparent; }

        .ms-sidebar-icon {
          width: 20px; height: 20px;
          border-radius: 4px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }

        .ms-sidebar-divider {
          height: 0.5px;
          background: var(--ms-border);
          margin: 8px 16px;
        }

        /* ── Main ── */
        .ms-main {
          background: var(--ms-bg);
          padding: 28px 32px;
          overflow-y: auto;
        }

        /* Quick actions */
        .ms-quick-row {
          display: flex;
          gap: 10px;
          margin-bottom: 32px;
          flex-wrap: wrap;
        }

        .ms-quick-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px 8px 12px;
          background: #fff;
          border: 0.5px solid var(--ms-border);
          border-radius: 4px;
          font-size: 15px;
          font-weight: 500;
          color: var(--ms-text);
          cursor: pointer;
          transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
        }
        .ms-quick-btn:hover {
          background: #f9f9f9;
          border-color: var(--ms-border-hover);
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }
        .ms-quick-btn.disabled { opacity: 0.4; cursor: default; }
        .ms-quick-btn.disabled:hover { background: #fff; border-color: var(--ms-border); box-shadow: none; }

        .ms-quick-dot {
          width: 8px; height: 8px;
          border-radius: 2px;
          flex-shrink: 0;
        }

        /* Section header */
        .ms-sec-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 14px;
        }
        .ms-sec-title {
          font-size: 15px;
          font-weight: 600;
          color: var(--ms-text);
          letter-spacing: -0.1px;
        }
        .ms-sec-action {
          font-size: 14px;
          color: #c8571a;
          cursor: pointer;
          font-weight: 500;
        }
        .ms-sec-action:hover { text-decoration: underline; }

        /* App cards */
        .ms-apps-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
          margin-bottom: 32px;
        }

        .ms-app-card {
          background: #fff;
          border: 0.5px solid var(--ms-border);
          border-radius: 6px;
          padding: 16px;
          cursor: pointer;
          transition: border-color 0.15s, box-shadow 0.15s;
          position: relative;
        }
        .ms-app-card:hover {
          border-color: var(--ms-border-hover);
          box-shadow: 0 2px 8px rgba(0,0,0,0.07);
        }
        .ms-app-card.disabled { opacity: 0.5; cursor: default; }
        .ms-app-card.disabled:hover { border-color: var(--ms-border); box-shadow: none; }

        .ms-app-top {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 10px;
        }

        .ms-app-icon-box {
          width: 36px; height: 36px;
          border-radius: 6px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }

        .ms-app-name {
          font-size: 16px;
          font-weight: 600;
          color: var(--ms-text);
          letter-spacing: -0.2px;
        }

        .ms-app-tag {
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--ms-text3);
          margin-bottom: 8px;
        }

        .ms-app-desc {
          font-size: 13.5px;
          line-height: 1.65;
          color: var(--ms-text2);
        }

        .ms-app-badge {
          position: absolute;
          top: 10px; right: 10px;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          padding: 2px 7px;
          border-radius: 10px;
          background: #f3f2f1;
          color: var(--ms-text3);
        }

        /* Hover accent bar on card */
        .ms-app-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 2px;
          border-radius: 6px 6px 0 0;
          opacity: 0;
          transition: opacity 0.2s;
        }
        .ms-app-card:not(.disabled):hover::before { opacity: 1; }

        /* Recent activity */
        .ms-recent-empty {
          padding: 28px 0;
          text-align: center;
          font-size: 14px;
          color: var(--ms-text3);
          background: #fff;
          border: 0.5px solid var(--ms-border);
          border-radius: 6px;
        }
        .ms-recent-empty-icon { margin-bottom: 8px; opacity: 0.35; }

        .ms-recent-list {
          background: #fff;
          border: 0.5px solid var(--ms-border);
          border-radius: 6px;
          overflow: hidden;
        }

        .ms-recent-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 16px;
          border-bottom: 0.5px solid var(--ms-border);
          cursor: pointer;
          transition: background 0.1s;
        }
        .ms-recent-row:last-child { border-bottom: none; }
        .ms-recent-row:hover { background: #f9f9f9; }

        .ms-recent-icon {
          width: 30px; height: 30px;
          border-radius: 5px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }

        .ms-recent-row:hover .rename-btn { opacity: 1 !important; }
        .ms-recent-info { flex: 1; min-width: 0; }
        .ms-recent-name {
          font-size: 15px;
          font-weight: 500;
          color: var(--ms-text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ms-recent-meta {
          font-size: 13px;
          color: var(--ms-text3);
          margin-top: 1px;
        }
        .ms-recent-time {
          font-family: 'JetBrains Mono', monospace;
          font-size: 12px;
          color: var(--ms-text3);
          white-space: nowrap;
          flex-shrink: 0;
        }

        .ms-rename-btn {
          opacity: 0;
          transition: opacity 0.15s;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0 3px;
          color: #a19f9d;
          font-size: 12px;
          line-height: 1;
          flex-shrink: 0;
        }
        .ms-recent-row:hover .ms-rename-btn { opacity: 1; }
        .ms-rename-row {
          display: flex;
          align-items: center;
          gap: 6px;
          width: 100%;
        }
        .ms-rename-input {
          font-size: 13px;
          padding: 3px 8px;
          border: 1.5px solid #0078d4;
          border-radius: 4px;
          outline: none;
          flex: 1;
          min-width: 0;
          font-family: 'Inter', sans-serif;
          color: var(--ms-text);
          background: #fff;
        }
        .ms-rename-save {
          font-size: 11px;
          color: #0078d4;
          cursor: pointer;
          font-weight: 600;
          white-space: nowrap;
          background: none;
          border: none;
          padding: 0;
        }
        .ms-rename-cancel {
          font-size: 11px;
          color: var(--ms-text3);
          cursor: pointer;
          white-space: nowrap;
          background: none;
          border: none;
          padding: 0;
        }

        @media (max-width: 700px) {
          .ms-layout { grid-template-columns: 1fr; }
          .ms-sidebar { display: none; }
          .ms-main { padding: 20px 16px; }
          .ms-apps-grid { grid-template-columns: 1fr 1fr; }
          .ms-topbar-search { display: none; }
        }

        body.dark .ms-root {
            --ms-bg: #1b1a19;
            --ms-surface: #252423;
            --ms-border: rgba(255,255,255,0.08);
            --ms-border-hover: rgba(255,255,255,0.14);
            --ms-text: #f3f2f1;
            --ms-text2: #b3b0ad;
            --ms-text3: #797775;
          }
          body.dark .ms-topbar { background: #252423; border-color: rgba(255,255,255,0.08); }
          body.dark .ms-sidebar { background: #252423; border-color: rgba(255,255,255,0.08); }
          body.dark .ms-sidebar-item:hover { background: #323130; }
          body.dark .ms-sidebar-item.active { background: #323130; }
          body.dark .ms-quick-btn { background: #252423; }
          body.dark .ms-quick-btn:hover { background: #323130; }
          body.dark .ms-app-card { background: #252423; }
          body.dark .ms-app-card:hover { background: #2d2c2b; }
          body.dark .ms-recent-list { background: #252423; }
          body.dark .ms-recent-row:hover { background: #2d2c2b; }
          body.dark .ms-recent-empty { background: #252423; }
          body.dark .ms-rename-input { background: #323130; color: #f3f2f1; border-color: #0078d4; }
          body.dark .ms-waffle:hover,
          body.dark .ms-icon-btn:hover { background: #323130; }


      `}</style>

      <div className="ms-root">

        {/* ── Top bar ── */}
        <div className="ms-topbar">
          <div className="ms-topbar-left">
            {/* Waffle */}
            <div className="ms-waffle">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <rect x="1" y="1" width="5" height="5" rx="1" /><rect x="7" y="1" width="5" height="5" rx="1" />
                <rect x="1" y="7" width="5" height="5" rx="1" /><rect x="7" y="7" width="5" height="5" rx="1" />
              </svg>
            </div>
            <img
              src="/lliuq.png"
              alt="lliuq"
              style={{
                height: 40,
                width: 40,
                borderRadius: 6,
                objectFit: "cover",
                objectPosition: "center top",  // crops to show just the icon part
                cursor: "pointer"
              }}
              onClick={() => navigate("/")}
            />

          </div>

          <input
            className="ms-topbar-search"
            type="text"
            placeholder="Search tools and history..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          <div className="ms-topbar-right">

            <a
              href="https://ko-fi.com/lliuqdeveloper"
              target="_blank"
              rel="noreferrer"
              className="ms-icon-btn"
              title="Support the Project"
              style={{ color: '#c8571a', marginRight: '4px' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l8.72-8.72 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </a>

            <div className="ms-icon-btn" title="Settings">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
                <circle cx="8" cy="8" r="2.5" />
                <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.3 3.3l1.4 1.4M11.3 11.3l1.4 1.4M3.3 12.7l1.4-1.4M11.3 4.7l1.4-1.4" />
              </svg>
            </div>


            <div className="ms-icon-btn" title="Help">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
                <circle cx="8" cy="8" r="6.5" />
                <path d="M6 6.2C6 5 7 4 8 4s2 .9 2 2c0 1.5-2 2-2 3.5" />
                <circle cx="8" cy="12" r="0.6" fill="currentColor" />
              </svg>
            </div>
          </div>
        </div>

        <div className="ms-layout">

          {/* ── Sidebar ── */}
          <div className="ms-sidebar">
            <div className="ms-sidebar-section">
              <div
                className={`ms-sidebar-item ${activeNav === "home" ? "active" : ""}`}
                onClick={() => setActiveNav("home")}
              >
                <div className="ms-sidebar-icon">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
                    <path d="M1 7l7-6 7 6v8H1z" /><path d="M6 15v-5h4v5" />
                  </svg>
                </div>

                Home
              </div>
              <div className="ms-sidebar-divider" />
              <div className="ms-sidebar-label">Community</div>
              <a
                href="https://ko-fi.com/lliuqdeveloper"
                target="_blank"
                rel="noreferrer"
                className="ms-sidebar-item"
                style={{ textDecoration: 'none' }}
              >
                <div className="ms-sidebar-icon" style={{ background: '#fff3ed', color: '#c8571a' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l8.72-8.72 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </div>
                Support Project
              </a>
            </div>

            <div
              className={`ms-sidebar-item${activeNav === "about" ? " active" : ""}`}
              onClick={() => { setActiveNav("about"); navigate("/about"); }}
            >
              <div className="ms-sidebar-icon">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="8" />
                  <line x1="12" y1="12" x2="12" y2="16" />
                </svg>
              </div>
              About
            </div>

            

            <div className="ms-sidebar-divider" />
            <div className="ms-sidebar-label">Apps</div>

            {TOOLS.map(t => (
              <div
                key={t.path}
                className={`ms-sidebar-item${t.disabled ? " disabled" : ""}${activeNav === t.name ? " active" : ""}`}
                onClick={() => { if (!t.disabled) { setActiveNav(t.name); navigate(t.path); } }}
              >
                <div className="ms-sidebar-icon" style={{ background: t.accentBg, color: t.accent }}>
                  {t.icon}
                </div>
                {t.name}
                {t.disabled && (
                  <span style={{ fontSize: 9, marginLeft: "auto", color: "#bbb", fontFamily: "monospace", letterSpacing: "0.05em" }}>SOON</span>
                )}
              </div>
            ))}

            <div className="ms-sidebar-divider" />
            <div className="ms-sidebar-label">API Key</div>
            {isEditing ? (
              <div style={{ padding: "6px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
                <input
                  type="password"
                  value={keyInput}
                  onChange={e => setKeyInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSaveKey()}
                  placeholder="gsk_..."
                  autoFocus
                  style={{ fontSize: 11, padding: "5px 8px", border: "1px solid var(--ms-border)", borderRadius: 4, outline: "none", fontFamily: "monospace", background: "var(--ms-bg)", color: "var(--ms-text)" }}
                />
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={handleSaveKey} disabled={!keyInput.trim().startsWith("gsk_")}
                    style={{ flex: 1, fontSize: 11, padding: "4px 8px", background: "#0078d4", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", opacity: keyInput.trim().startsWith("gsk_") ? 1 : 0.4 }}>
                    Save
                  </button>
                  <button onClick={() => setIsEditing(false)}
                    style={{ fontSize: 11, padding: "4px 8px", background: "rgba(0,0,0,0.05)", border: "none", borderRadius: 4, cursor: "pointer" }}>
                    ✕
                  </button>
                </div>
              </div>
            ) : groqKey ? (
              <div className="ms-sidebar-item" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
                <span style={{ fontSize: 11, color: "#107c10", fontWeight: 500 }}>✓ Key saved</span>
                <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                  <span style={{ color: "#e06050", fontSize: 10, cursor: "pointer", background: "rgba(224,96,80,0.1)", padding: "3px 8px", borderRadius: 4, border: "1px solid rgba(224,96,80,0.2)", fontFamily: "monospace" }} onClick={handleClearKey}>Clear ×</span>
                  <span style={{ color: "#0078d4", fontSize: 10, cursor: "pointer", background: "rgba(0,120,212,0.1)", padding: "3px 8px", borderRadius: 4, border: "1px solid rgba(0,120,212,0.2)", fontFamily: "monospace" }} onClick={() => { setIsEditing(true); setKeyInput(groqKey); }}>Edit</span>
                </div>
              </div>
            ) : (
              <div style={{ padding: "6px 12px" }}>
                <button
                  onClick={() => setIsEditing(true)}
                  style={{ width: "100%", fontSize: 11, padding: "6px 8px", background: "#0078d4", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}
                >
                  Set Groq Key
                </button>
                <div style={{ marginTop: 8 }}>
                  <a href="https://console.groq.com/keys" target="_blank" rel="noreferrer" style={{ fontSize: 10, color: "#0078d4" }}>Get free key →</a>
                </div>
              </div>
            )}

            <div className="ms-sidebar-divider" />
            <div className="ms-sidebar-label">Storage</div>

            <div className="ms-sidebar-item" onClick={handleClearHistory} style={{ color: "#c8571a" }}>
              <div className="ms-sidebar-icon">
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="#c8571a" strokeWidth="1.4">
                  <polyline points="3 4 13 4" /><path d="M5 4V2h6v2M6 7v5M10 7v5M4 4l1 10h6l1-10" />
                </svg>
              </div>
              Clear history
            </div>
          </div>

          {/* ── Main ── */}
          <div className="ms-main">

            {/* Quick actions */}
            <div className="ms-quick-row">
              {TOOLS.map(t => (
                <div
                  key={t.path}
                  className={`ms-quick-btn${t.disabled ? " disabled" : ""}`}
                  onClick={() => !t.disabled && navigate(t.path)}
                >
                  <div className="ms-quick-dot" style={{ background: t.disabled ? "#ccc" : t.accent }} />
                  New {t.name} session
                  {t.disabled && <span style={{ fontSize: 10, color: "#bbb", fontWeight: 400 }}>· soon</span>}
                </div>
              ))}
            </div>

            {/* Apps */}
            <div className="ms-sec-header">
              <div className="ms-sec-title">Your tools</div>
            </div>

            <div className="ms-apps-grid">
              {TOOLS.filter(t =>
                t.name.toLowerCase().includes(search.toLowerCase()) ||
                t.tag.toLowerCase().includes(search.toLowerCase())
              ).map(t => (
                <div
                  key={t.path}
                  className={`ms-app-card${t.disabled ? " disabled" : ""}`}
                  onClick={() => !t.disabled && navigate(t.path)}
                  onMouseEnter={() => setHoveredTool(t.path)}
                  onMouseLeave={() => setHoveredTool(null)}
                  style={{
                    "--card-accent": t.accent,
                  }}
                >
                  {/* Top accent bar */}
                  <div style={{
                    position: "absolute", top: 0, left: 0, right: 0,
                    height: 2, borderRadius: "6px 6px 0 0",
                    background: t.accent,
                    opacity: hoveredTool === t.path && !t.disabled ? 1 : 0,
                    transition: "opacity 0.2s",
                  }} />

                  {t.disabled && <div className="ms-app-badge">Soon</div>}

                  <div className="ms-app-top">
                    <div className="ms-app-icon-box" style={{ background: t.accentBg, color: t.accent }}>
                      {t.icon}
                    </div>
                    <div className="ms-app-name">{t.name}</div>
                  </div>
                  <div className="ms-app-tag">{t.tag}</div>
                  <div className="ms-app-desc">{t.desc}</div>
                </div>
              ))}
            </div>

            {/* Recent activity */}
            <div className="ms-sec-header">
              <div className="ms-sec-title">Recent activity</div>
              {history.length > 0 && (
                <div className="ms-sec-action" onClick={handleClearHistory}>Clear all</div>
              )}
            </div>

            {history.length === 0 ? (
              <div className="ms-recent-empty">
                <div className="ms-recent-empty-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3">
                    <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z" />
                    <path d="M8 12h8M12 8v8" />
                  </svg>
                </div>
                No sessions yet — start with Scribe or Interview above.
              </div>
            ) : (
              <div className="ms-recent-list">
                {history
                  .filter(item =>
                    !search ||
                    item.title?.toLowerCase().includes(search.toLowerCase()) ||
                    item.subtitle?.toLowerCase().includes(search.toLowerCase()) ||
                    item.tool?.toLowerCase().includes(search.toLowerCase())
                  )
                  .slice(0, showMore ? undefined : 10)
                  .map((item, i) => {
                    const tool = toolMap[item.tool] || TOOLS[0];
                    return (
                      <div
                        key={i}
                        className="ms-recent-row"
                        onClick={() => navigate(item.id ? `${tool.path}?session=${item.id}` : tool.path)}
                      >
                        <div className="ms-recent-icon" style={{ background: tool.accentBg, color: tool.accent }}>
                          {tool.icon}
                        </div>
                        <div className="ms-recent-info">
                          {renamingIndex === i ? (
                            <div className="ms-rename-row" onClick={e => e.stopPropagation()}>
                              <input
                                autoFocus
                                className="ms-rename-input"
                                value={renameValue}
                                onChange={e => setRenameValue(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === "Enter") handleRename(i);
                                  if (e.key === "Escape") setRenamingIndex(null);
                                }}
                              />
                              <button className="ms-rename-save" onClick={() => handleRename(i)}>Save</button>
                              <button className="ms-rename-cancel" onClick={() => setRenamingIndex(null)}>Cancel</button>
                            </div>
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <div className="ms-recent-name">{item.title || "Untitled session"}</div>
                              <button
                                className="ms-rename-btn"
                                title="Rename"
                                onClick={e => {
                                  e.stopPropagation();
                                  setRenamingIndex(i);
                                  setRenameValue(item.title || "");
                                }}
                              >✏️</button>
                            </div>
                          )}
                          <div className="ms-recent-meta">{tool.name} · {item.subtitle || tool.tag}</div>
                        </div>
                        <div className="ms-recent-time">{timeAgo(item.ts)}</div>
                      </div>
                    );
                  })}
              </div>
            )}
            {history.length > 10 && (
              <div style={{ textAlign: "center", padding: "10px 0" }}>
                <span onClick={() => setShowMore(s => !s)}
                  style={{ fontSize: 12, color: "#0078d4", cursor: "pointer", fontWeight: 500 }}>
                  {showMore ? "Show less ↑" : `Show ${history.length - 10} more ↓`}
                </span>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
}